const Keys = {
  Backspace: 8,
}

export const PinInput = {
  activeInputIndex: null,
  codeLength: null,
  codeLengthRegexp: null,
  isActiveInputEmpty(index) {
    const input = this.getPinInputs()[this.activeInputIndex]

    if (input) {
      return input.value === ''
    } else {
      return false
    }
  },
  mounted() {
    // This is the expected length of the pin/code.
    this.codeLength = Number(this.el.dataset.codeLength)
    this.codeLengthRegex = new RegExp(`\\d{${this.codeLength}}`)
    const inputs = this.getPinInputs()
    const inputsCount = inputs.length

    if (inputsCount !== this.codeLength) {
      this.pushEvent('pin-input-error', {
        message: `Error! Expected ${this.codeLength} pin inputs but found ${inputsCount}`,
      })
    }

    // Sets the focus to the first input.
    this.setActiveInput(0)

    /*
     * This handles Safari's OTP autofill using the `input` event on the first
     * input. Mobile Safari does not honor the `maxlength` property of an input
     * when it's doing an autofill of an OTP code. For posterity Safari fires
     * an `input` event for each character of the OTP code on the target input,
     * adding characters one at a time, e.g. `0`, `01`, `012`, `0123`. Because
     * we're already listening for the change event using Phoenix, and because
     * there are multiple events fired it makes more sense to use the `input`
     * event.
     *  */
    const firstInput = inputs[0]
    firstInput.addEventListener('input', e => {
      const inputs = this.getPinInputs()
      const code = (e.target.value.match(this.codeLengthRegex) || [''])[0]

      if (code.length === this.codeLength) {
        code.split('').forEach((char, index) => {
          inputs[index].value = char
        })

        this.pushEvent('pin-input-paste', { code })

        /*
         * The submit button needs be focused or mobile Safari may hide the
         * the value in the second input, making it look empty, until another
         * element is focused. This also tends to make the inputs focus in the
         * the correct order as they're filled out. It's weird.
         * */
        const submitButton = document.querySelector('button[type="submit"]')
        submitButton.focus()
      }
    })

    /*
     * We need to set up some event listeners on the individual inputs within
     * the container:
     *
     *   1. A 'focus' event listener to adjust the `activeInputIndex` when the
     *      user selects a specific input by click or touch.
     *   2. A `paste` event listener to take the contents of the clipboard and
     *      and put a single character in each input.
     * */
    inputs.forEach(input => {
      input.addEventListener('focus', e => {
        const index = this.getPinInputs().indexOf(e.target)
        if (this.activeInputIndex !== index) {
          this.setActiveInput(index)
        }
      })

      input.addEventListener('paste', e => {
        // We need to prevent default here otherwise the first input will
        // gobble up the paste data and only the first character will show up.
        e.preventDefault()

        const inputs = this.getPinInputs()
        const data = (event.clipboardData || window.clipboardData).getData('text')
        // First, we'll attempt to extract the correct amount of digits from
        // the paste data. This is in case the user copies the entire message
        // with the one-time-password.
        const code = (data.match(this.codeLengthRegex) || [''])[0]

        // Next we check the code is the expected length to guard against the
        // empty string from above.
        if (code.length === this.codeLength) {
          // Now we'll populate each of the inputs with the characters in the
          // code using each characters index to select the correct input. This
          // is necessary because the `preventDefault` call above will cause the
          // first input to be blank (even after firing off the `pushEvent` to
          // send the data to the LiveView).
          code.split('').forEach((char, index) => {
            inputs[index].value = char
          })

          // Finally, we push the code to the LiveView, and the button should
          // now be enabled.
          this.pushEvent('pin-input-paste', { code })
        }
      })
    })

    /*
     * This event listener allows the user to use the backspace key to move
     * backwards through the inputs clearing the previous input with each press.
     * It requires the currently focused input to be *empty* before moving
     * backwards. This is to allow users to delete the character in the last
     * input and enter a new one.
     *
     * tldr; If all inputs are filled out, the last input requires two presses
     * of the delete key to get to the second to last character.
     * */
    this.el.addEventListener('keydown', e => {
      if (e.keyCode === Keys.Backspace && this.isActiveInputEmpty()) {
        this.handleBackspace()
      }
    })
  },
  updated() {
    /*
     * Updates happen when the user types something into an input field and is
     * triggered by the phx-change event on the form... OR! When Safari's OTP
     * autofill is used, and if Safari's OTP autofill is used-- we need to
     * use the lastInputIndex because `this.el.dataset.selectedInputIndex` will
     * be `NaN`.
     * */
    const lastInputIndex = this.getPinInputs().length - 1
    const activeInputIndex = Number(this.el.dataset.selectedInputIndex || lastInputIndex)

    // This is what pushes the focus to the next input.
    this.setActiveInput(activeInputIndex)
  },
  setActiveInput(index) {
    const element = this.getPinInputs()[index]
    if (element) {
      this.activeInputIndex = index
      element.focus()
      element.select()
    }
  },
  handleBackspace() {
    const index = this.activeInputIndex - 1
    const previousInput = this.getPinInputs()[index]

    if (previousInput) {
      previousInput.value = ''
      // This sends an event to the LiveView to remove the character that was
      // deleted when the user hit backspace.
      this.pushEvent('pin-input-cleared', { index })
      this.setActiveInput(index)
    }
  },
  getPinInputs() {
    return Array.from(this.el.children).filter(
      child => child.tagName === 'INPUT' && child.type !== 'hidden',
    )
  },
}
